import { ReactElement, useEffect, useState } from 'react'
import { supabase } from '@services/supabase'
import { apiFetch } from '@utils/apiConfig'
import { useAuth } from '@hooks/useAuth'

type AccessState = 'checking' | 'allowed' | 'denied' | 'expired' | 'unavailable'

/** Verify current account and school status before mounting a dashboard. */
export function AccountAccessGate({ userId, children }: {
  userId?: string
  children: ReactElement
}) {
  const { session } = useAuth()
  const token = session?.access_token
  const [access, setAccess] = useState<AccessState>('checking')
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!userId || !token) return
    const controller = new AbortController()
    let cancelled = false
    setAccess('checking')
    const timeout = setTimeout(() => {
      controller.abort()
      if (!cancelled) setAccess('unavailable')
    }, 15000)

    const verify = async () => {
      try {
        // Account verification needs only the current session token. Resolving dashboard
        // context first adds RPC/profile dependencies to the gate that protects them.
        const response = await apiFetch('/api/auth/me', {
          signal: controller.signal,
          headers: { Authorization: `Bearer ${token}` },
        })
        if (cancelled || controller.signal.aborted) return
        if (response.status === 401) {
          setAccess('expired')
        } else if (response.status === 403) {
          setAccess('denied')
        } else if (!response.ok) {
          setAccess('unavailable')
        } else {
          const profile = await response.json()
          if (!cancelled && !controller.signal.aborted) {
            setAccess(profile?.id === userId ? 'allowed' : 'unavailable')
          }
        }
      } catch {
        if (!cancelled) setAccess('unavailable')
      } finally {
        clearTimeout(timeout)
      }
    }
    void verify()
    return () => {
      cancelled = true
      clearTimeout(timeout)
      controller.abort()
    }
  }, [userId, token, attempt])

  if (access === 'allowed') return children

  const signInAgain = async () => {
    try {
      const { error } = await supabase.auth.signOut({ scope: 'local' })
      if (error) throw error
      window.location.assign('/login')
    } catch {
      setAccess('unavailable')
    }
  }

  const messages = {
    checking: ['Checking account access', 'Please wait while we check your account.'],
    denied: ['Account access unavailable', 'Your account or school access is not currently available. Please contact your school administrator or Qwixl support.'],
    expired: ['Please sign in again', 'Your session has expired. Sign in to continue.'],
    unavailable: ['Unable to verify access', 'We could not check your account right now. Please try again.'],
  }
  const [title, description] = messages[access]
  return (
    <main className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 px-4">
      <section className="max-w-md rounded-xl border border-gray-200 dark:border-gray-700 p-6 text-gray-900 dark:text-gray-100" aria-live="polite">
        <h1 className="text-xl font-semibold mb-3">{title}</h1>
        <p className="mb-5 text-sm">{description}</p>
        {access !== 'checking' && (
          <div className="flex flex-wrap gap-3">
            {access !== 'expired' && (
              <button type="button" className="rounded bg-blue-600 px-4 py-2 text-white" onClick={() => setAttempt(value => value + 1)}>Try again</button>
            )}
            <button type="button" className="rounded border px-4 py-2" onClick={signInAgain}>Sign in again</button>
            {access === 'denied' && <a className="px-4 py-2 underline" href="mailto:support@qwixl.com">Contact Qwixl support</a>}
          </div>
        )}
      </section>
    </main>
  )
}
